// src/components/ContainersSidebar.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaHome, FaCalendarAlt, FaUserFriends, FaCog, FaPlus, FaRocket, FaAddressBook,
} from "react-icons/fa";
import Contatos from "./Contatos";
import { supabase } from "../supabaseClient";
import Collab from "./Collab";
import ContainerSettings from "./ContainerSettings";
import Agenda from "./Agenda";
import { images } from "../config/images";
import "./ContainersSidebar.css";

const getConsistentColor = (str) => {
  if (!str) return "#1a2332";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 32%)`;
};

export default function ContainersSidebar({
  user,
  containerAtual,
  setContainerAtual,
  onCreateProject,
  onUnreadCountChange,
  onOpenNovidades,
}) {
  const navigate = useNavigate();

  const [showCollab, setShowCollab] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);
  const [colaboradores, setColaboradores] = useState([]);
  const [showContatos, setShowContatos] = useState(false);
  const realtimeRef = useRef(null);

  const fetchNotificacoesNaoLidas = useCallback(async () => {
    if (!user?.id || !user?.email) { setNotificacoesNaoLidas(0); return; }
    try {
      const { data: convitesPendentes } = await supabase
        .from("convites")
        .select("id", { count: "exact" })
        .eq("email", user.email)
        .eq("status", "pendente");
      const { data: mencoesNaoLidas } = await supabase
        .from("notificacoes")
        .select("id", { count: "exact" })
        .eq("user_id", user.id)
        .eq("lido", false);
      const total = (convitesPendentes?.length || 0) + (mencoesNaoLidas?.length || 0);
      setNotificacoesNaoLidas(total);
    } catch (err) {
      console.error("Erro ao buscar notificações não lidas:", err);
      setNotificacoesNaoLidas(0);
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    onUnreadCountChange?.(notificacoesNaoLidas);
  }, [notificacoesNaoLidas, onUnreadCountChange]);

  const fetchColaboradores = useCallback(async () => {
    if (!user?.id || !user?.email) return;
    try {
      const { data: convitesAceitos } = await supabase
        .from("convites")
        .select("*")
        .eq("email", user.email)
        .eq("status", "aceito");
      const colaboradoresComPerfil = await Promise.all(
        (convitesAceitos || []).map(async (c) => {
          const { data: remetente } = await supabase
            .from("profiles")
            .select("id,nome,email,avatar_url,container")
            .eq("id", c.remetente_id)
            .maybeSingle();
          return { ...c, remetente };
        })
      );
      setColaboradores(colaboradoresComPerfil);
    } catch (err) {
      console.error("Erro ao buscar colaboradores:", err);
      setColaboradores([]);
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    if (!user?.id || !user?.email) return;
    fetchNotificacoesNaoLidas();
    fetchColaboradores();

    const convitesChannel = supabase
      .channel("convites-pendentes")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "convites", filter: `email=eq.${user.email},status=eq.pendente` }, () => fetchNotificacoesNaoLidas())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "convites", filter: `email=eq.${user.email}` }, (payload) => {
        if (payload.new.status === "pendente" || payload.old?.status === "pendente") fetchNotificacoesNaoLidas();
      })
      .subscribe();

    const mencaoChannel = supabase
      .channel("mencoes-nao-lidas")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notificacoes", filter: `user_id=eq.${user.id},tipo=eq.menção` }, () => fetchNotificacoesNaoLidas())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notificacoes", filter: `user_id=eq.${user.id},tipo=eq.menção` }, (payload) => {
        if (payload.old?.lido !== payload.new.lido) fetchNotificacoesNaoLidas();
      })
      .subscribe();

    realtimeRef.current = { convitesChannel, mencaoChannel };
    return () => {
      if (realtimeRef.current) {
        supabase.removeChannel(realtimeRef.current.convitesChannel);
        supabase.removeChannel(realtimeRef.current.mencaoChannel);
      }
    };
  }, [user?.id, user?.email, fetchNotificacoesNaoLidas, fetchColaboradores]);

  const handleVoltarHome = () => {
    if (user?.id) {
      setContainerAtual(user.id);
      navigate(`/containers/${user.id}`);
    }
  };

  const handleTrocarContainer = (colaborador) => {
    const containerId = colaborador?.remetente?.id;
    if (!containerId) return;
    setContainerAtual(containerId);
    navigate(`/containers/${containerId}`);
  };

  const isHomeAtiva = containerAtual === user?.id;

  return (
    <>
      <aside className="cx-sidebar">
        <div className="cx-sidebar__logo">
          <img src={images.ob2} alt="OnBox" className="cx-sidebar__logo-img" />
        </div>

        <div className="cx-sidebar__addwrap">
          <button className="cx-sidebar__addbtn" onClick={() => onCreateProject?.()}>
            <FaPlus size={12} />
            <span>Nova Box</span>
          </button>
        </div>

        <div className="cx-sidebar__divider" />

        <nav className="cx-sidebar__nav">
          <button className={`cx-sidebar__navitem ${isHomeAtiva ? "cx-sidebar__navitem--active" : ""}`} onClick={handleVoltarHome}>
            <FaHome size={14} /><span>Home</span>
          </button>
          <button className="cx-sidebar__navitem" onClick={() => setShowAgenda(true)}>
          <FaCalendarAlt size={14} /><span>Agenda</span>
          </button>
          <button className="cx-sidebar__navitem" onClick={() => setShowContatos(true)}>
            <FaAddressBook size={14} /><span>Contatos</span>
          </button>
          <button className="cx-sidebar__navitem" onClick={() => setShowCollab(true)}>
            <FaUserFriends size={14} /><span>Colaboração</span>
          </button>
          <button className="cx-sidebar__navitem" onClick={() => setShowSettings(true)}>
            <FaCog size={14} /><span>Permissões</span>
          </button>
        </nav>

        <div className="cx-sidebar__divider" />

        <div className="cx-sidebar__section cx-sidebar__section--grow">
          <span className="cx-sidebar__section-label">Colaborações</span>
          <div className="cx-sidebar__favlist">
            <div
              className={`cx-sidebar__favitem ${isHomeAtiva ? "cx-sidebar__favitem--active" : ""}`}
              onClick={handleVoltarHome}
            >
              <div className="cx-sidebar__favicon" style={{ background: "#1a2332" }}>
                {(user?.email?.charAt(0) || "?").toUpperCase()}
              </div>
              <span>Meu container</span>
            </div>

            {colaboradores.map((c) => (
              <div
                key={c.id}
                className={`cx-sidebar__favitem ${containerAtual === c.remetente?.id ? "cx-sidebar__favitem--active" : ""}`}
                onClick={() => handleTrocarContainer(c)}
              >
                <div
                  className="cx-sidebar__favicon"
                  style={{ background: c.remetente?.avatar_url ? "transparent" : getConsistentColor(c.remetente?.id) }}
                >
                  {c.remetente?.avatar_url ? (
                    <img src={c.remetente.avatar_url} alt={c.remetente?.nome} />
                  ) : (
                    c.remetente?.nome?.charAt(0)?.toUpperCase() || "?"
                  )}
                </div>
                <span>{c.remetente?.nome || "Colaborador"}</span>
              </div>
            ))}

            {colaboradores.length === 0 && (
              <span className="cx-sidebar__empty"></span>
            )}
          </div>
        </div>

        <div className="cx-sidebar__divider" />

        <button className="cx-sidebar__news-btn" onClick={() => onOpenNovidades?.()}>
            <FaRocket className="cx-sidebar__news-icon" />
            <span>Novidades OnBox</span>
            </button>
      </aside>

      {showCollab && (
        <Collab
          onClose={() => { setShowCollab(false); fetchColaboradores(); fetchNotificacoesNaoLidas(); }}
          user={user}
          onOpenTask={(data) => {
            const { tipo_navegacao, container_id, projeto_id, setor_id, pilha_id, nota_id } = data;
            if (container_id && container_id !== containerAtual) {
              setContainerAtual(container_id);
              navigate(`/containers/${container_id}`);
            }
            if (tipo_navegacao === "unidade" && (projeto_id || setor_id)) {
              const entityId = projeto_id || setor_id;
              const queryParams = new URLSearchParams({ entityId, type: setor_id ? "setor" : "project", containerId: container_id });
              navigate(`/cards/${entityId}?${queryParams.toString()}`, {
                state: { projectId: projeto_id, setorId: setor_id, entityType: setor_id ? "setor" : "project", containerId: container_id },
              });
            } else if (tipo_navegacao === "pilha" && pilha_id) {
              const entityId = projeto_id || setor_id;
              const queryParams = new URLSearchParams({ entityId, type: setor_id ? "setor" : "project", containerId: container_id });
              navigate(`/cards/${entityId}?${queryParams.toString()}`, {
                state: { projectId: projeto_id, setorId: setor_id, pilhaId: pilha_id, entityType: setor_id ? "setor" : "project", containerId: container_id },
              });
            } else if (tipo_navegacao === "nota" && nota_id) {
              const entityId = projeto_id || setor_id;
              const queryParams = new URLSearchParams({ entityId, type: setor_id ? "setor" : "project", containerId: container_id, nota: nota_id });
              navigate(`/cards/${entityId}?${queryParams.toString()}`, {
                state: { projectId: projeto_id, setorId: setor_id, pilhaId: pilha_id, notaId: nota_id, entityType: setor_id ? "setor" : "project", containerId: container_id, openNota: true },
              });
            }
            fetchNotificacoesNaoLidas();
            setShowCollab(false);
          }}
        />
      )}

      {showContatos && (
        <Contatos onClose={() => setShowContatos(false)} user={user} containerAtual={containerAtual} />
      )}

      {showSettings && (
        <ContainerSettings onClose={() => setShowSettings(false)} containerId={user?.id} user={user} />
      )}

      {showAgenda && (
        <Agenda user={user} currentContainerId={containerAtual} onClose={() => setShowAgenda(false)} />
      )}
    </>
  );
}